/**
 * VamRescheduleBanner — Sprint 11 S11.5 (motor v8 §3).
 *
 * Banner one-shot que aparece en PlanPage cuando el backend acaba de
 * reprogramar un TEST-VAM no completado (+4 días). El backend emite
 * el flag transitorio `plan.recent_vam_reschedule` en GET
 * /training-plans/active SOLO cuando la reprogramación ocurrió en
 * ese mismo request; al siguiente refresh, si no hay nuevo missed
 * test, el flag desaparece y el banner no se renderiza.
 *
 * NO persiste el dismissal en BD ni localStorage:
 * - Refresh siguiente: backend ya no emite el flag (test ya en el
 *   futuro) → banner ausente por ausencia del prop.
 * - Si el atleta dismissea y luego refresh-a: banner ausente
 *   (porque flag tampoco vendría).
 * - El estado `isDismissed` solo controla el render dentro del mismo
 *   ciclo de vida del componente. Patrón one-shot puro.
 *
 * Estilo warning soft (warning/30) — coherente con TestVamProtocolBlock
 * (S11.3) y postpone warning (S10.1b).
 */
import { useState } from "react";

import { formatDateForVamLabel } from "@/lib/format";
import { t } from "@/i18n/es";


export interface VamRescheduleBannerProps {
  /** ISO date YYYY-MM-DD de la fecha original del test (informativo —
   *  no se renderiza por ahora, reservado para evolución del copy). */
  oldDate: string;
  /** ISO date YYYY-MM-DD de la nueva fecha del test. Se renderiza
   *  formateada DD/MM/AAAA en el body del banner. */
  newDate: string;
}

export function VamRescheduleBanner({ newDate }: VamRescheduleBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) return null;

  const body = t.plan.vamRescheduleBannerBody.replace(
    "{date}",
    formatDateForVamLabel(newDate),
  );

  return (
    <section
      data-testid="vam-reschedule-banner"
      role="status"
      aria-live="polite"
      className="rounded-md border border-warning/30 bg-warning/10 p-4"
    >
      <p className="text-sm font-medium text-warning">
        {t.plan.vamRescheduleBannerTitle}
      </p>
      <p
        data-testid="vam-reschedule-banner-body"
        className="mt-1 text-sm text-bone-muted"
      >
        {body}
      </p>
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          data-testid="vam-reschedule-banner-dismiss"
          onClick={() => setIsDismissed(true)}
          className="rounded-md border border-border-subtle px-3 py-1 text-xs text-bone-muted hover:border-border-strong hover:text-bone"
        >
          {t.plan.vamRescheduleBannerDismiss}
        </button>
      </div>
    </section>
  );
}
